/**
 * Building blocks for the Connector admin page, in the TaskManagement shape:
 * header row, clickable stat cards, a search + Filters toolbar, a right-hand filter drawer,
 * and the person cell that replaces every raw user id on the page.
 */
import type { ReactNode } from 'react';
import { IconFilter, IconTrash } from '@tabler/icons-react';
import { Group, Stack, Title, Text, Card, ThemeIcon, Paper, Button, Badge, TextInput, Drawer, Divider, Avatar } from '@/components/ui';

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <Group justify="space-between" wrap="wrap" align="flex-start">
      <Stack gap={0}>
        <Title order={2}>{title}</Title>
        {subtitle && <Text c="dimmed" size="sm">{subtitle}</Text>}
      </Stack>
      {actions && <Group gap="sm">{actions}</Group>}
    </Group>
  );
}

export function StatsCard({ icon, color, value, label, active, onClick }: { icon: ReactNode; color: string; value: number | string; label: string; active?: boolean; onClick?: () => void }) {
  return (
    <Card
      withBorder padding="md" radius="md"
      style={{
        cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s ease',
        borderColor: active ? `var(--mantine-color-${color}-5)` : undefined,
        backgroundColor: active ? `var(--mantine-color-${color}-light)` : undefined,
      }}
      onClick={onClick}
    >
      <Group>
        <ThemeIcon size="lg" radius="md" variant={active ? 'filled' : 'light'} color={color}>{icon}</ThemeIcon>
        <Stack gap={0}>
          <Text size="xl" fw={700}>{value}</Text>
          <Text size="xs" c="dimmed">{label}</Text>
        </Stack>
      </Group>
    </Card>
  );
}

export function FilterToolbar({
  search, onSearch, searchPlaceholder = 'Search…', activeFilterCount = 0, onOpenFilters, onClearAll, rightSection,
}: {
  search: string; onSearch: (v: string) => void; searchPlaceholder?: string;
  activeFilterCount?: number; onOpenFilters?: () => void; onClearAll: () => void; rightSection?: ReactNode;
}) {
  const hasActive = activeFilterCount > 0 || search.trim() !== '';
  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" wrap="wrap" gap="md">
        <Group gap="md" wrap="wrap" style={{ flex: 1, minWidth: 260 }}>
          <TextInput placeholder={searchPlaceholder} value={search} onChange={(e) => onSearch(e.target.value)} style={{ minWidth: 250 }} />
          {onOpenFilters && (
            <Button variant={activeFilterCount > 0 ? 'light' : 'default'} color={activeFilterCount > 0 ? 'blue' : undefined} leftSection={<IconFilter size={16} />} onClick={onOpenFilters}>
              Filters
              {activeFilterCount > 0 && <Badge size="sm" variant="filled" color="blue" ml="xs">{activeFilterCount}</Badge>}
            </Button>
          )}
          {hasActive && <Button variant="subtle" color="red" size="sm" leftSection={<IconTrash size={14} />} onClick={onClearAll}>Clear All</Button>}
        </Group>
        {rightSection && <Group gap="sm" wrap="nowrap">{rightSection}</Group>}
      </Group>
    </Paper>
  );
}

/** "Showing x of y things" — sits directly above the table. */
export function ResultsCount({ shown, total, noun }: { shown: number; total: number; noun: string }) {
  return <Text size="sm" c="dimmed">Showing {shown} of {total} {noun}</Text>;
}

export function FilterDrawer({ opened, onClose, onClearAll, hasActiveFilters, children, title = 'Filters' }: {
  opened: boolean; onClose: () => void; onClearAll: () => void; hasActiveFilters: boolean; children: ReactNode; title?: string;
}) {
  return (
    <Drawer opened={opened} onClose={onClose} title={<Group><IconFilter size={20} /><Text fw={600}>{title}</Text></Group>} position="right" size="sm" padding="lg">
      <Stack gap="lg">
        {children}
        <Divider />
        <Group grow><Button variant="light" color="red" leftSection={<IconTrash size={16} />} onClick={onClearAll} disabled={!hasActiveFilters}>Clear All Filters</Button></Group>
        <Button fullWidth onClick={onClose}>Apply Filters</Button>
      </Stack>
    </Drawer>
  );
}

/** Avatar + name + email. The one way a person is shown on this page; the user id never is. */
export function PersonCell({ name, email, avatar, size = 'sm' }: { name?: string | null; email?: string | null; avatar?: string | null; size?: 'sm' | 'md' }) {
  const display = name?.trim() || email || 'Unknown user';
  return (
    <Group gap="sm" wrap="nowrap">
      <Avatar src={avatar || undefined} radius="xl" size={size}>{initials(display)}</Avatar>
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="sm" fw={500} truncate>{display}</Text>
        {email && email !== display && <Text size="xs" c="dimmed" truncate>{email}</Text>}
      </Stack>
    </Group>
  );
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

/** Distinct, sorted select options from whatever the data actually contains. */
export function optionsFrom<T>(rows: T[], pick: (r: T) => string | null | undefined): { value: string; label: string }[] {
  const set = new Set<string>();
  for (const r of rows) { const v = pick(r)?.trim(); if (v) set.add(v); }
  return [...set].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
}
